import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import axios from 'axios';
import {
  LayoutDashboard, CreditCard, Settings, LogOut, Shield,
  CheckCircle, XCircle, Eye, Image, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPayments() {
  const { token, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [payments, setPayments] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchPayments();
  }, [token, navigate, filterStatus]);

  const fetchPayments = async () => {
    try {
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const response = await axios.get(`${API}/admin/payments${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
      showNotification('Erro ao carregar pagamentos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const approvePayment = async (paymentId) => {
    try {
      await axios.put(
        `${API}/admin/payments/${paymentId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showNotification('Pagamento aprovado e usuário ativado!');
      fetchPayments();
    } catch (error) {
      showNotification('Erro ao aprovar pagamento', 'error');
    }
  };

  const rejectPayment = async (paymentId) => {
    try {
      await axios.put(
        `${API}/admin/payments/${paymentId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showNotification('Pagamento rejeitado');
      fetchPayments();
    } catch (error) {
      showNotification('Erro ao rejeitar pagamento', 'error');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800 border-amber-200',
      approved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      rejected: 'bg-red-100 text-red-800 border-red-200'
    };
    const labels = { pending: 'Pendente', approved: 'Aprovado', rejected: 'Rejeitado' };
    
    return (
      <Badge variant="outline" className={styles[status]}>
        {labels[status]}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
          notification.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
        }`}>
          {notification.type === 'error' ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {notification.message}
        </div>
      )}

      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-bold">Admin</h1>
              <p className="text-xs text-slate-400">VoiceIdeas</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          <Link to="/admin/dashboard">
            <Button 
              variant={location.pathname === '/admin/dashboard' ? 'secondary' : 'ghost'} 
              className="w-full justify-start text-white hover:text-white hover:bg-slate-800"
            >
              <LayoutDashboard className="w-4 h-4 mr-3" />
              Dashboard
            </Button>
          </Link>
          <Link to="/admin/pagamentos">
            <Button 
              variant={location.pathname === '/admin/pagamentos' ? 'secondary' : 'ghost'}
              className="w-full justify-start text-white hover:text-white hover:bg-slate-800"
            >
              <CreditCard className="w-4 h-4 mr-3" />
              Pagamentos
            </Button>
          </Link>
          <Link to="/admin/configuracoes">
            <Button 
              variant={location.pathname === '/admin/configuracoes' ? 'secondary' : 'ghost'}
              className="w-full justify-start text-white hover:text-white hover:bg-slate-800"
            >
              <Settings className="w-4 h-4 mr-3" />
              Configurações
            </Button>
          </Link>
        </nav>
        
        <div className="p-4 border-t border-slate-800">
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-3" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Pagamentos</h1>
              <p className="text-muted-foreground">Gerencie os comprovantes de pagamento</p>
            </div>
            <select 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-40 h-10 px-3 rounded-md border border-input bg-background text-sm"
              data-testid="payment-status-filter"
            >
              <option value="all">Todos</option>
              <option value="pending">Pendentes</option>
              <option value="approved">Aprovados</option>
              <option value="rejected">Rejeitados</option>
            </select>
          </div>

          {/* Payments Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Comprovantes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Usuário</TableHead>
                      <TableHead>E-mail</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Comprovante</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id} data-testid={`payment-row-${payment.id}`}>
                        <TableCell className="font-medium">{payment.user_name}</TableCell>
                        <TableCell>{payment.user_email}</TableCell>
                        <TableCell>{getStatusBadge(payment.status)}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(payment.created_at).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedReceipt(payment.receipt_image);
                              setShowModal(true);
                            }}
                            data-testid={`view-receipt-${payment.id}`}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Ver
                          </Button>
                        </TableCell>
                        <TableCell className="text-right">
                          {payment.status === 'pending' && (
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-emerald-600"
                                onClick={() => approvePayment(payment.id)}
                                data-testid={`approve-payment-${payment.id}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-600"
                                onClick={() => rejectPayment(payment.id)}
                                data-testid={`reject-payment-${payment.id}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {payments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          Nenhum pagamento encontrado
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Receipt Preview Modal */}
      {showModal && selectedReceipt && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2">
                <Image className="w-5 h-5" />
                <h3 className="font-semibold">Comprovante de Pagamento</h3>
              </div>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => {
                  setShowModal(false);
                  setSelectedReceipt(null);
                }}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-80px)]">
              <img 
                src={selectedReceipt} 
                alt="Comprovante" 
                className="w-full object-contain rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
